import { Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { floatNumOfBins } from 'common/constants';
import FilterHistogram, { Bins } from 'components/FilterHistogram';
import FilterSlider from 'components/FilterSlider';
import { FilterValues } from 'features/resultFiltering/resultFilteringSlice';
import { SearchResultsGroupMember } from 'features/searchMolecules/searchMoleculesSlice';
import { useCalculations } from 'hooks';
import clamp from 'lodash/clamp';
import isEqual from 'lodash/isEqual';
import React, { useState } from 'react';
import { some, useConditionalEffect, useDeltaArray } from 'react-delta';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(5),
    },
    heading: { marginBottom: theme.spacing(1), textAlign: 'center', fontWeight: 'bold' },
  }),
);

const clampSliderValues = (vs: number[], min: number, max: number) => {
  return vs.map((v) => clamp(v, min, max));
};

export interface Props {
  property: { name: string; description: string };
  sliderMin: number;
  sliderMax: number;
  resultsMin: number;
  resultsMax: number;
  data: [SearchResultsGroupMember[], SearchResultsGroupMember[]];
  numFiltered: number;
  lowerChanged: boolean;
  upperChanged: boolean;
  initialValues: FilterValues;
  changeValues: (_: FilterValues) => void;
}

const Filter = ({
  property,
  sliderMin,
  sliderMax,
  resultsMin,
  resultsMax,
  data: [dataInRange, dataNotInRange],
  numFiltered,
  initialValues,
  changeValues,
  lowerChanged,
  upperChanged,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const calculations = useCalculations();

  const [values, setValues] = useState<FilterValues>(initialValues);

  const handleChange = (vs: FilterValues) => {
    setValues(clampSliderValues(vs, sliderMin, sliderMax) as FilterValues);
  };

  const handleChangeCommit = (vs: FilterValues) => {
    const clampedValues = clampSliderValues(vs, sliderMin, sliderMax) as FilterValues;
    setValues(clampedValues);
    changeValues(clampedValues);
  };

  const isInteger = calculations.find((calc) => calc.name === property.name)?.type === 'integer';

  // Update local state of filters when the initialValues changes
  // E.g the filters have been reset
  const deltaInitialValues = useDeltaArray(initialValues); // Track changes in the initial values prop
  useConditionalEffect(() => {
    setValues(initialValues);
  }, some(deltaInitialValues));

  // Bins are calculated differently depending of the dtype
  let bins: Bins;
  if (isInteger) {
    // Bins centred on the integer values
    bins = { start: sliderMin - 0.5, end: sliderMax + 0.5, size: 1 };
  } else {
    // Bins fit inside result data range
    bins = { start: sliderMin, end: sliderMax, size: (sliderMax - sliderMin) / floatNumOfBins };
  }

  return (
    <div className={classes.root}>
      <Typography
        id={`${property.name}-slider`}
        variant="subtitle1"
        className={classes.heading}
        display="block"
      >
        {property.description ?? property.name} ({numFiltered})
      </Typography>
      {sliderMin !== sliderMax ? (
        <>
          <FilterHistogram
            width={160}
            height={60}
            min={isInteger ? sliderMin - 0.5 : sliderMin}
            max={isInteger ? sliderMax + 0.5 : sliderMax}
            dataInRange={dataInRange.map((member) => member.calculations[property.name])}
            dataNotInRange={dataNotInRange.map((member) => member.calculations[property.name])}
            bins={bins}
          />
          <FilterSlider
            property={property}
            isInteger={isInteger}
            values={[...values]}
            min={sliderMin}
            max={sliderMax}
            marks={[resultsMin, resultsMax]}
            lowerChanged={lowerChanged}
            upperChanged={upperChanged}
            handleChange={handleChange}
            handleChangeCommit={handleChangeCommit}
          />
        </>
      ) : (
        <Typography variant="body1" gutterBottom>
          {t('hapax_legomenon')}
        </Typography>
      )}
    </div>
  );
};

const filterPropsComparator = (
  { changeValues: f, ...prevProps }: Props,
  { changeValues: g, ...nextProps }: Props,
) => {
  return isEqual(prevProps, nextProps);
};

export default React.memo(Filter, filterPropsComparator);
